<script setup lang="ts">
import CanvasButton from "@/components/canvas/CanvasButton.vue";
import { Logger } from "@/logger";
import { Facade, DraggerType } from "@/drawing";
import DraggerMenuState from "@/stores/DraggerMenuState";
import BasePointToolState from "@/stores/BasePointToolState";
import KeypadToggleBtn from "@/components/canvas/Tools/options/KeypadToggleBtn.vue";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import EditTextBtn from "./EditTextBtn.vue";
import RotateImageBtn from "./RotateImageBtn.vue";
import ScaleDropdown from "./ScaleDropdown.vue";
import PhotoMarkerSizeDropdown from "./PhotoMarkerSizeDropdown.vue";
import PhotoMarkerValueInput from "./PhotoMarkerValueInput.vue";
import ChangeLayerBtn from "./ChangeLayerBtn.vue";
import TransformDropdown from "./TransformDropdown.vue";
import {
  trackSelection,
  type SelectionAction,
} from "@/analytics/drawing-analytics";
import DynBlockPropertiesPanel from "./DynBlockPropertiesPanel.vue";
import BlockAttributesPanel from "./BlockAttributesPanel.vue";

defineProps<{
  /** Écran étroit : la barre passe ses outils à la ligne. */
  sheet?: boolean;
}>();

const BASE_POINT_ACTIONS: Partial<Record<DraggerType, SelectionAction>> = {
  [DraggerType.Copy]: "copy_from_point",
  [DraggerType.Move]: "move_from_point",
  [DraggerType.Rotate]: "rotate_from_point",
  [DraggerType.Scale]: "scale_from_point",
};

function deleteSelection() {
  Logger.info(`ActionMenu.vue : deleteSelection`);
  trackSelection("delete", { source: "menu" });
  if (Facade.isInitialized) {
    Facade.selection.deleteSelected();
  }
}

function copySelection() {
  Logger.info(`ActionMenu.vue : copySelection`);
  trackSelection("duplicate", { source: "menu" });
  if (Facade.isInitialized) {
    Facade.selection.duplicateSelected();
  }
}

// Launches one of the interactive base-point edit tools (copy/move/rotate/
// scale), bypassing DraggerMenuState.setActiveDragger() on purpose: that helper
// clears the current selection right after activating the tool, which would
// destroy the very selection these tools need to operate on.
function activateBasePointTool(type: DraggerType) {
  Logger.info(`ActionMenu.vue : activateBasePointTool ${type}`);
  // Base-point tools belong to the selection bar family: they are only
  // reachable from here and bypass setActiveDragger (it would clear the
  // selection they need).
  trackSelection(BASE_POINT_ACTIONS[type] ?? "move_from_point");
  if (Facade.isInitialized) {
    Facade.tools.activate(type);
    BasePointToolState.active = type;
    subscribeToPendingPlacement();
  }
}

function finishBasePointTool() {
  Logger.info(`ActionMenu.vue : finishBasePointTool`);
  if (Facade.isInitialized) {
    Facade.tools.current?.cancelPendingPlacement();
  }
}

function escape() {
  Logger.info(`ActionMenu.vue : escape`);
  trackSelection("escape");
  if (Facade.isInitialized) {
    Facade.selection.clear();
    // Untracked on purpose: going through finishAndContinue() logged every
    // Échap a second time as "finish_polyline".
    finishDrawing();
  }
}

function finishAndContinue() {
  Logger.info(`ActionMenu.vue : finishAndContinue`);
  trackSelection("finish_polyline", { source: "menu" });
  finishDrawing();
}

function finishDrawing() {
  if (Facade.isInitialized) {
    // Finish the current polyline drawing (this keeps the dragger active for a new polyline)
    Facade.tools.finishDrawing();
  }
}

function cancelPendingPlacement() {
  Logger.info(`ActionMenu.vue : cancelPendingPlacement`);
  if (Facade.isInitialized) {
    // Discards the pending point A of a two-point (Rectangle/Ellipse/Line
    // sequential mode) placement — the touch/tablet equivalent of Escape.
    Facade.tools.current?.cancelPendingPlacement();
  }
}

const selectedCount = ref(0);
const targets = ref<string[]>([]);

const updateSelection = () => {
  if (!Facade.isInitialized) return;
  const newCount = Facade.selection.count;
  const newHandles = Facade.selection.handles;
  selectedCount.value = newCount;
  targets.value = newHandles;
};

let unsubscribe: (() => void) | undefined;
let initCheckInterval: ReturnType<typeof setInterval> | undefined;

const trySubscribe = () => {
  if (Facade.isInitialized && !unsubscribe) {
    unsubscribe = Facade.selection.onChange(updateSelection);
    updateSelection(); // Update immediately after subscribing
    subscribeToPendingPlacement();

    // Clear the interval if it's running
    if (initCheckInterval) {
      clearInterval(initCheckInterval);
      initCheckInterval = undefined;
    }
  }
};

// Whether the active dragger has an uncommitted point A (two-point mode),
// used to show a floating cancel button — touch/tablet has no Escape key.
const hasPendingTwoPointPlacement = ref(false);
let unsubscribePendingPlacement: (() => void) | undefined;

// Which base-point edit tool is currently active, if any. Kept in a store
// (rather than read off DraggerMenuState.activeDragger.name, which these tools
// deliberately never touch — see activateBasePointTool()) because the keypad
// panel needs the same signal; cleared by the same onPendingPlacementChange
// callback used below, since BasePointDragger.hasPendingPlacement() is true for
// its entire active lifetime.
const activeBasePointTool = computed(() => BasePointToolState.active);

// Copy keeps every copy it has already placed, so its button confirms
// ("Finish"); the others commit nothing until their last point, so theirs
// aborts ("Cancel").
const isRepeatingTool = computed(
  () => activeBasePointTool.value === DraggerType.Copy,
);

function subscribeToPendingPlacement() {
  unsubscribePendingPlacement?.();
  unsubscribePendingPlacement = undefined;
  hasPendingTwoPointPlacement.value = false;

  if (!Facade.isInitialized) return;
  const dragger = Facade.tools.current;
  if (!dragger) return;

  hasPendingTwoPointPlacement.value = dragger.hasPendingPlacement();
  unsubscribePendingPlacement = dragger.onPendingPlacementChange((pending) => {
    hasPendingTwoPointPlacement.value = pending;
    if (!pending) {
      BasePointToolState.active = null;
    }
  });
}

// Facade.tools.current changes identity when the active tool changes, so
// re-subscribe whenever that happens (mode toggles within the same tool
// don't need this — DualModeDragger's subscription to it stays stable).
watch(
  () => DraggerMenuState.activeDragger.name,
  () => subscribeToPendingPlacement(),
);

onMounted(() => {
  // Try to subscribe immediately
  trySubscribe();

  // If not initialized yet, poll until it is
  if (!Facade.isInitialized) {
    initCheckInterval = setInterval(() => {
      trySubscribe();
    }, 100);
  }
});

onBeforeUnmount(() => {
  if (unsubscribe) {
    unsubscribe();
  }
  unsubscribePendingPlacement?.();
  if (initCheckInterval) {
    clearInterval(initCheckInterval);
  }
});

const isPolylineActive = computed(() => {
  return DraggerMenuState.activeDragger.name === DraggerType.Polyline;
});

const isEditable = computed(() => {
  if (!Facade.isInitialized || targets.value.length !== 1) return false;
  return Facade.text.canEdit();
});

const isScalable = computed(() => {
  // Scale dropdown is shown only for text entities (single selection)
  // where we can change the text height
  return selectedCount.value === 1 && isEditable.value;
});

// These capabilities ask the Facade about the CURRENT selection, which is not
// reactive: they must depend on `targets` (a fresh array on every selection change)
// and not on `selectedCount`, which stays 1 when one entity replaces another and
// would leave the previous entity's buttons on screen.
const isPhotoMarkerResizable = computed(() => {
  if (!Facade.isInitialized || targets.value.length !== 1) return false;
  return Facade.photoMarkers.canResizeSelected();
});

// The attribute shows a formatted date/time in "date" mode — editing it as a
// free-text value no longer makes sense, so the number input is hidden.
const isPhotoMarkerNumberEditable = computed(() => {
  return DraggerMenuState.getPhotoMarkerAttribute() !== "date";
});

// A single dynamic block reference is selected: expose its custom properties.
// Always false on engines older than DrawingWeb 27.7.
const isDynamicBlock = computed(() => {
  if (!Facade.isInitialized || targets.value.length !== 1) return false;
  return Facade.dynBlocks.canEditSelected();
});

// A single raster image (photo, PDF page): quarter-turn button.
const isImage = computed(() => {
  if (!Facade.isInitialized || targets.value.length !== 1) return false;
  return Facade.images.canRotateSelected();
});

// A single block carrying text attributes (photo markers excluded — they have
// their own input above).
const hasBlockAttributes = computed(() => {
  if (!Facade.isInitialized || targets.value.length !== 1) return false;
  return Facade.blocks.canEditAttributesSelected();
});
</script>

<template>
  <!--
  hasPendingTwoPointPlacement / isPolylineActive come before the selection
  menu on purpose: a just-created shape stays auto-selected, so while
  placing the *next* shape's point A (or drawing the next polyline),
  selectedCount can still be > 0 from the previous one. The in-progress
  action takes priority over stale selection tools for that old shape.
  -->
  <div
    v-if="activeBasePointTool"
    class="canvas-menu canvas-bottom-bar canvas-selection-bar text-white d-flex flex-column gap-1"
  >
    <div class="d-flex gap-1 p-1 align-items-center">
      <CanvasButton
        :icon="isRepeatingTool ? 'check-lg' : 'x-lg'"
        size="nosize"
        @click="finishBasePointTool()"
        :tooltipText="
          isRepeatingTool
            ? $t('canvas:selection.finishCopy.tooltip.text')
            : $t('canvas:selection.cancelTransform.tooltip.text')
        "
        :tooltipTitle="
          isRepeatingTool
            ? $t('canvas:selection.finishCopy.tooltip.title')
            : $t('canvas:selection.cancelTransform.tooltip.title')
        "
        ><div
          class="ms-2 text-center d-flex align-items-center fw-normal"
          style="height: 20px; font-size: 11px"
        >
          {{
            isRepeatingTool
              ? $t("canvas:selection.finishCopy.label")
              : $t("canvas:selection.escape.label")
          }}
        </div></CanvasButton
      >
      <!--
      The step prompt ("pick a base point"…) is shown by ToolHintBar, not here.
      The pad opens itself as soon as a step accepts typed values (see
      TypedInputPanel), so this is the way back in after closing it — and it
      stays hidden while the tool is still waiting for its base point, which
      only a pick can give.
      -->
      <KeypadToggleBtn v-if="BasePointToolState.canTypeInput" />
    </div>
  </div>
  <!--
  Polyligne : le bouton Terminer n'a de sens que pendant un tracé (au moins un
  sommet posé) — hasPendingTwoPointPlacement est alimenté par le même contrat
  hasPendingPlacement()/onPendingPlacementChange que les outils à deux points.
  -->
  <div
    v-else-if="isPolylineActive && hasPendingTwoPointPlacement"
    class="canvas-menu canvas-bottom-bar canvas-selection-bar text-white d-flex flex-column gap-1"
  >
    <div class="d-flex gap-1 p-1">
      <CanvasButton
        icon="check-lg"
        size="nosize"
        @click="finishAndContinue()"
        :tooltipText="$t('canvas:selection.finishPolyline.tooltip.text')"
        :tooltipTitle="$t('canvas:selection.finishPolyline.tooltip.title')"
        ><div
          class="ms-2 text-center d-flex align-items-center fw-normal"
          style="height: 20px; font-size: 11px"
        >
          {{ $t("canvas:selection.escape.label") }}
        </div></CanvasButton
      >
    </div>
  </div>
  <div
    v-else-if="hasPendingTwoPointPlacement"
    class="canvas-menu canvas-bottom-bar canvas-selection-bar text-white d-flex flex-column gap-1"
  >
    <div class="d-flex gap-1 p-1">
      <CanvasButton
        icon="x-lg"
        size="nosize"
        @click="cancelPendingPlacement()"
        :tooltipText="$t('canvas:selection.cancelPlacement.tooltip.text')"
        :tooltipTitle="$t('canvas:selection.cancelPlacement.tooltip.title')"
        ><div
          class="ms-2 text-center d-flex align-items-center fw-normal"
          style="height: 20px; font-size: 11px"
        >
          {{ $t("canvas:selection.escape.label") }}
        </div></CanvasButton
      >
    </div>
  </div>
  <div
    v-else-if="!!selectedCount"
    class="canvas-menu canvas-bottom-bar canvas-selection-bar text-white d-flex flex-column gap-1"
    :class="{ 'selection-sheet': sheet }"
  >
    <!--div class="text-center">
      <span v-if="selectedCount === 1">{{
        $t("canvas:selection.modal.one")
      }}</span>
      <span v-else
        >{{ selectedCount }} {{ $t("canvas:selection.modal.several") }}</span
      >
    </div-->
    <div class="selection-tools d-flex gap-1 p-1 align-items-center">
      <!-- Group 1: basic operations -->
      <CanvasButton
        icon="trash"
        @click="deleteSelection()"
        :tooltipText="$t('canvas:selection.delete.tooltip.text')"
        :tooltipTitle="$t('canvas:selection.delete.tooltip.title')"
      >
      </CanvasButton>
      <CanvasButton
        icon="copy"
        @click="copySelection()"
        :tooltipText="$t('canvas:selection.duplicate.tooltip.text')"
        :tooltipTitle="$t('canvas:selection.duplicate.tooltip.title')"
      >
      </CanvasButton>

      <div class="canvas-divider-vertical" />

      <!-- Group 2: transform with base point (copy, move, rotate, scale) -->
      <TransformDropdown @select="activateBasePointTool" />

      <div class="canvas-divider-vertical" />

      <!-- Group 3: change properties -->
      <ScaleDropdown v-if="isScalable" :target="targets[0]" />
      <PhotoMarkerSizeDropdown
        v-if="isPhotoMarkerResizable"
        :target="targets[0]"
      />
      <PhotoMarkerValueInput
        v-if="isPhotoMarkerResizable && isPhotoMarkerNumberEditable"
        :target="targets[0]"
      />
      <EditTextBtn v-if="isEditable" :target="targets[0]" />
      <RotateImageBtn v-if="isImage" />
      <DynBlockPropertiesPanel v-if="isDynamicBlock" :target="targets[0]" />
      <BlockAttributesPanel v-if="hasBlockAttributes" :target="targets[0]" />
      <ChangeLayerBtn :targets="targets" />

      <!-- Sortie : en bout de barre, tenue a l'ecart des actions, en bleu
           pour etre trouvee d'un coup d'oeil -->
      <div class="canvas-divider-vertical ms-3" />
      <CanvasButton
        class="escape-btn"
        size="nosize"
        active
        @click="escape()"
        :tooltipText="$t('canvas:selection.escape.tooltip.text')"
        :tooltipTitle="$t('canvas:selection.escape.tooltip.title')"
      >
        <span class="escape-label">{{
          $t("canvas:selection.escape.label")
        }}</span>
      </CanvasButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.canvas-menu {
  background-color: var(--canvas-bg);
  border: 1px solid var(--canvas-border);
}

// Posee sur le bord bas du canvas, d'ou elle sort : coins arrondis en haut
// seulement, pas de bordure basse.
.canvas-bottom-bar {
  border-bottom: none;
  border-radius: 12px 12px 0 0;
  animation: canvas-bottom-bar-rise 0.18s ease-out;
}

// Barre active (selection, outil base-point, placement en cours, polyline) :
// liseré bleu, fond legerement bleute, halo discret.
.canvas-selection-bar {
  border-color: var(--canvas-selection-border);
  background:
    linear-gradient(var(--canvas-selection-bg), var(--canvas-selection-bg)),
    var(--canvas-bg);
  box-shadow:
    0 0 0 1px var(--canvas-selection-border),
    0 -2px 20px var(--canvas-selection-glow);
}

// Écran étroit : même barre, ses outils passent à la ligne (centrés, sans
// séparateurs) au lieu de déborder de l'écran.
.selection-sheet {
  max-width: 100%;
  padding-bottom: env(safe-area-inset-bottom);

  .selection-tools {
    flex-wrap: wrap;
    justify-content: center;
    row-gap: 4px;
  }

  .canvas-divider-vertical {
    display: none;
  }
}

.escape-btn {
  height: 36px;
  padding: 0 14px;
  border-radius: 10px;
}

.escape-label {
  font-size: 13px;
  font-weight: 700;
}

@keyframes canvas-bottom-bar-rise {
  from {
    transform: translateY(100%);
    opacity: 0.4;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>
